/**
 * ЗНАЧКИ РАЗДЕЛОВ
 *
 * Раньше в полосе разделов стояли эмодзи. Каждый из них нарисован
 * своим художником в своей манере: 🛒 объёмная и серая, 💊 плоская
 * и розовая, 🎬 чёрная с бликом. Рядом друг с другом десять таких
 * значков выглядят как случайная россыпь наклеек, а не как один
 * интерфейс — и на разных телефонах ещё и по-разному, потому что
 * эмодзи рисует система.
 *
 * Здесь они нарисованы одной линией одной толщины и берут цвет
 * у текста рядом. Скучнее по отдельности — спокойнее вместе.
 *
 * Толщина 1,75 подобрана под размер 22: при 1,5 значки блёкнут рядом
 * с полужирными подписями, при 2 — спорят с ними за внимание.
 */

type Props = {
  name: IconName
  size?: number
  className?: string
}

export type IconName =
  | 'chats'
  | 'feed'
  | 'shopping'
  | 'planner'
  | 'health'
  | 'recipes'
  | 'budget'
  | 'wallet'
  | 'documents'
  | 'wishes'
  | 'movies'
  | 'more'
  | 'settings'
  | 'family'
  | 'bell'
  | 'admin'
  | 'search'
  | 'image'
  | 'camera'
  | 'file'
  | 'place'
  | 'poll'
  | 'round'
  | 'exit'

/** Пути нарисованы в сетке 24×24 — та же, что у значков в шапках. */
const PATHS: Record<IconName, string> = {
  // Переписка: облачко с хвостиком влево-вниз
  chats: 'M20 12a7.5 7.5 0 0 1-7.5 7.5H5.5L4 21v-4.2A7.5 7.5 0 1 1 20 12z',

  // Что нового: колокольчик
  feed: 'M18 9a6 6 0 1 0-12 0c0 5-2 6-2 6h16s-2-1-2-6M13.7 20a2 2 0 0 1-3.4 0',

  // Покупки: корзина с ручкой
  shopping: 'M4 7h16l-1.4 10.2a2 2 0 0 1-2 1.8H7.4a2 2 0 0 1-2-1.8L4 7zm4 0a4 4 0 0 1 8 0',

  // Планер: лист календаря
  planner: 'M5 6h14a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1zM4 10h16M8 3v4M16 3v4',

  // Здоровье: капсула под наклоном
  health: 'M9.5 4.5a4.2 4.2 0 0 1 6 6l-5 5a4.2 4.2 0 0 1-6-6l5-5zM7 7l6 6',

  // Еда: кастрюля с ручками
  recipes: 'M4 10h16v5a4 4 0 0 1-4 4H8a4 4 0 0 1-4-4v-5zM4 12H2.5M20 12h1.5M8 7V5M12 7V4.5M16 7V5',

  // Бюджет: кошелёк с застёжкой
  budget: 'M4 8a2 2 0 0 1 2-2h11a1 1 0 0 1 1 1v1M4 8v9a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2M4 8h14a2 2 0 0 1 2 2v2h-4a2 2 0 0 1 0-4h4',

  // Кошелёк: билет с надрывом посередине
  wallet: 'M4 8a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v2.5a1.8 1.8 0 0 0 0 3.5V17a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-3a1.8 1.8 0 0 0 0-3.5V8zM14 7v2M14 12v2M14 17v-2',

  // Документы: папка с уголком
  documents: 'M4 7a1 1 0 0 1 1-1h4l2 2h8a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7z',

  // Желания: коробка с лентой
  wishes: 'M4 11h16v8a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-8zM3 8h18v3H3V8zM12 8v12M12 8S9.5 4 7.5 4a2 2 0 0 0 0 4M12 8s2.5-4 4.5-4a2 2 0 0 1 0 4',

  // Кино: хлопушка
  movies: 'M3 9h18v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9zM3 9l1.5-4 16.5 1.5L21 9M8.5 5.5 7 9M13.5 6 12 9',

  more: 'M6 12h.01M12 12h.01M18 12h.01',

  settings: 'M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7zM19.4 13.5a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-2.7 1.1v.3a2 2 0 1 1-4 0v-.2a1.6 1.6 0 0 0-2.8-1.1l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.6 1.6 0 0 0-1.1-2.7h-.3a2 2 0 1 1 0-4h.2a1.6 1.6 0 0 0 1.1-2.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.6 1.6 0 0 0 2.7-1.1v-.3a2 2 0 1 1 4 0v.2a1.6 1.6 0 0 0 2.8 1.1l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.6 1.6 0 0 0 1.1 2.7h.3a2 2 0 1 1 0 4h-.2a1.6 1.6 0 0 0-1.5 1.1z',

  // Семья: двое рядом
  family: 'M9 11a3.2 3.2 0 1 0 0-6.4A3.2 3.2 0 0 0 9 11zM3 20v-1.5A4.5 4.5 0 0 1 7.5 14h3A4.5 4.5 0 0 1 15 18.5V20M16 4.8a3.2 3.2 0 0 1 0 6.2M21 20v-1.5a4.5 4.5 0 0 0-3.2-4.3',

  bell: 'M18 9a6 6 0 1 0-12 0c0 5-2 6-2 6h16s-2-1-2-6M13.7 20a2 2 0 0 1-3.4 0',

  // Состояние сервера: стойка с полками
  admin: 'M4 5h16a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1zM4 14h16a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1zM7 7.5h.01M7 16.5h.01',

  exit: 'M15 4h3a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1h-3M11 16l4-4-4-4M15 12H4',

  // Лупа: поиск внутри переписки
  search: 'M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14zM20 20l-4-4',

  // Снимок: рамка, солнце, горы
  image: 'M4 5h16a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1zM8.5 10h.01M3.5 16l4.5-4.5 4 4 3-2.5 5 4.5',

  // Камера: корпус с объективом
  camera: 'M3 8h3l1.5-2h9L18 8h3a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1zM12 16.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z',

  // Лист с загнутым углом
  file: 'M14 3H7a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V7l-4-4zM14 3v4h4',

  // Метка на карте
  place: 'M12 21s7-5.5 7-11a7 7 0 1 0-14 0c0 5.5 7 11 7 11zM12 12.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z',

  // Опрос: три полоски разной длины
  poll: 'M5 6h14M5 12h9M5 18h5',

  // Кружок: камера в круге
  round: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM10 9.5l5 2.5-5 2.5z',
}

export function Icon({ name, size = 22, className }: Props) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={PATHS[name]} />
    </svg>
  )
}
